import { soadReportFormPart1DataSchema, soadReportFormPart2DataSchema, soadReportFormPart3DataSchema, soadReportFormPart4DataSchema, soadReportFormPart5DataSchema, soadReportFormPart6DataSchema, } from "../../schemas/soad/srf.js";
import { assessmentDelayCauseOptions, assessmentDelayDetailFields, minAssessmentDelayDays, } from "../../schemas/soad/srfAssessmentDelays.js";
const soadSigning = {
    userOrTeamRequirement: { accreditation: [], teamType: ["soad"] },
};
const assessmentsField = {
    type: "multiple-copies",
    titleFn: (index) => `Additional Assessment ${(index + 1).toString()}`,
    addCopyLabel: "Add another assessment",
    removeCopyLabel: "Remove assessment",
    field: "otherAssessments",
    fields: [
        // AD-1.3.1
        {
            type: "date",
            label: "Date of assessment",
            field: "dateOfAssessment",
            maximum: (now) => now,
        },
        {
            type: "branch",
            field: "assessmentConducted",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    hideLabel: true,
                    fieldsOwned: [
                        "assessmentType",
                        "assessmentTypeText",
                        "assessmentAddress",
                    ],
                    fields: [
                        // AD-1.3.2
                        {
                            type: "dropdown",
                            valueField: "assessmentType",
                            textField: "assessmentTypeText",
                            label: "How was the assessment of the patient conducted?",
                            options: [
                                { label: "In Person", value: "inPerson" },
                                { label: "Phone Call", value: "phoneCall" },
                                { label: "Video Call", value: "videoCall" },
                            ],
                        },
                        {
                            type: "branch",
                            field: "assessmentType",
                            branches: [
                                // AD-1.3.2.1
                                {
                                    fieldValue: "inPerson",
                                    fields: [
                                        {
                                            type: "name-address",
                                            addressLabel: "Where did the assessment take place?",
                                            disableName: true,
                                            field: "assessmentAddress",
                                        },
                                    ],
                                    fieldsOwned: [
                                        "assessmentType",
                                        "assessmentTypeText",
                                        "assessmentAddress",
                                    ],
                                    hideLabel: true,
                                    label: "Other",
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        // AD-1.3.3
        {
            type: "textbox",
            label: "Reason for additional assessment",
            field: "reasonForAssessment",
            rows: "5",
        },
    ],
};
const additionalPersonField = {
    type: "multiple-copies",
    titleFn: (index) => `Additional Person ${(index + 1).toString()}`,
    addCopyLabel: "Add person",
    removeCopyLabel: "Delete person",
    field: "additionalPerson",
    fields: [
        // CD-1.9.1
        {
            type: "textbox",
            label: "Name of Additional Person",
            field: "nameOfAdditionalPerson",
        },
        // CD-1.9.2
        {
            type: "textbox",
            label: "Relationship to the patient",
            field: "relationshipToPatient",
        },
    ],
};
export const patientConsentStatusFields = ({ possibleConflict, }) => [
    {
        type: "label",
        label: ["Patient Consent"],
        labelType: "subtitle",
    },
    // SA-1.10
    {
        type: "branch",
        label: "What is your conclusion regarding the patient’s consent?",
        field: "patientConsentStatus",
        branches: [
            {
                fieldValue: "capable",
                label: "I certify that the patient is capable of understanding the nature, purpose and likely effects of the certified treatment and has consented to it.",
                fieldsOwned: [],
                fields: [],
            },
            {
                fieldValue: "notCapable",
                label: "I certify that the patient is not capable of understanding the nature, purpose and likely effects of the certified treatment.",
                fieldsOwned: possibleConflict ? ["notCapableConfirmationConsent"] : [],
                fields: [
                    ...(possibleConflict
                        ? [
                            // SA-1.10.1
                            {
                                type: "branch",
                                label: "Are you aware of any conflicts with valid and applicable advance decisions, or donee, deputy or court of protection?",
                                field: "notCapableConfirmationConsent",
                                branches: [
                                    {
                                        fieldValue: true,
                                        label: "Yes",
                                        fieldsOwned: [],
                                        fields: [],
                                    },
                                    {
                                        fieldValue: false,
                                        label: "No",
                                        fieldsOwned: [],
                                        fields: [],
                                    },
                                ],
                            },
                        ]
                        : []),
                ],
            },
            {
                fieldValue: "noConsent",
                label: "I certify that the patient is capable of understanding the nature, purpose and likely effects of the treatment and has not consented to it.",
                fieldsOwned: [],
                fields: [],
            },
            {
                fieldValue: "cto",
                label: "CTO Patient.",
                fieldsOwned: [],
                fields: [],
            },
        ],
        warningBanner: {
            message: "Under the conditions you have outlined, ECT should only be given if it is considered urgent treatment, falling within s62(1)(a) or (b), (assuming Part 4 applies to the patient):\n(a) it is immediately necessary to save the patient's life\n(b) (not being irreversible) is immediately necessary to prevent a serious deterioration of their condition.",
            condition: (data) => data.patientConsentStatus === "noConsent" ||
                (data.patientConsentStatus === "notCapable" &&
                    data.notCapableConfirmationConsent),
        },
    },
];
export const authorisingEctFields = [
    // SA-1.5
    {
        type: "textbox",
        label: "Describe the ECT authorised",
        field: "planAuthorisedEct",
        rows: "5",
    },
    // SA-1.6
    {
        type: "branch",
        label: "Are you authorising more than 12 applications of ECT?",
        field: "moreThan12Applications",
        branches: [
            {
                fieldValue: true,
                label: "Yes",
                fieldsOwned: [],
                fields: [],
            },
            {
                fieldValue: false,
                label: "No",
                fieldsOwned: [],
                fields: [],
            },
        ],
    },
];
export const authorisingMedicationFields = [
    // SA-1.7
    {
        type: "textbox",
        label: "Describe the medication authorised",
        field: "planAuthorisedMedication",
        rows: "5",
        afterLabel: "The input in this field can be copied directly into the description of treatment section of the Statutory Certificate when completing it.",
    },
    // SA-1.8
    {
        type: "branch",
        label: "Have you authorised any drug dosage above BNF limits?",
        field: "bnfLimits",
        branches: [
            {
                fieldValue: true,
                label: "Yes",
                fieldsOwned: ["bnfLimitsFor"],
                fields: [
                    // SA-1.8.1
                    {
                        type: "branch",
                        label: "Was this for…",
                        field: "bnfLimitsFor",
                        branches: [
                            {
                                fieldValue: "singleDrug",
                                label: "A single drug",
                                fieldsOwned: [],
                                fields: [],
                            },
                            {
                                fieldValue: "drugsInCombination",
                                label: "Drugs in combination",
                                fieldsOwned: [],
                                fields: [],
                            },
                        ],
                    },
                ],
            },
            {
                fieldValue: false,
                label: "No",
                fieldsOwned: [],
                fields: [],
            },
        ],
    },
    // SA-1.9
    {
        type: "branch",
        label: "Have you authorised off-label medication?",
        field: "offLabelMedication",
        branches: [
            {
                fieldValue: true,
                label: "Yes",
                fieldsOwned: [],
                fields: [],
            },
            {
                fieldValue: false,
                label: "No",
                fieldsOwned: [],
                fields: [],
            },
        ],
    },
];
export const approvedPlan = (childFields, childFieldsOwned) => [
    // SA-1.4
    {
        type: "branch",
        label: "Please select the plan which has been approved",
        field: "treatmentPlanDecision",
        branches: [
            {
                fieldValue: "current",
                label: "Current treatment at the time of visit",
                fieldsOwned: [...childFieldsOwned],
                fields: [
                    {
                        type: "label",
                        label: ["Detail of the plan you are authorising"],
                        labelType: "subtitle",
                    },
                    ...childFields,
                ],
            },
            {
                fieldValue: "proposed",
                label: "The Approved Clinician's proposed treatment plan",
                fieldsOwned: [...childFieldsOwned],
                fields: [
                    {
                        type: "label",
                        label: ["Detail of the plan you are authorising"],
                        labelType: "subtitle",
                    },
                    ...childFields,
                ],
            },
            {
                fieldValue: "modified",
                label: "I am making changes to the plan",
                fieldsOwned: ["changesMade", "extentOfChanges", ...childFieldsOwned],
                fields: [
                    // SA-1.4.1
                    {
                        type: "branch",
                        label: "What are the extent of changes you are making to the plan?",
                        field: "extentOfChanges",
                        branches: [
                            {
                                fieldValue: "minor",
                                label: "Slight",
                                fieldsOwned: [],
                                fields: [],
                            },
                            {
                                fieldValue: "major",
                                label: "Significant",
                                fieldsOwned: [],
                                fields: [],
                            },
                        ],
                    },
                    // SA-1.3.2
                    {
                        type: "textbox",
                        label: "Describe the changes made to the treatment plan",
                        field: "changesMade",
                        rows: "5",
                    },
                    {
                        type: "label",
                        label: ["Detail of the plan you are authorising"],
                        labelType: "subtitle",
                    },
                    ...childFields,
                ],
            },
            {
                fieldValue: "notApproved",
                label: "Plan not approved, no certificate issued",
                fieldsOwned: ["notApprovedReason"],
                fields: [
                    // SA-1.4.3
                    {
                        type: "textbox",
                        label: "Briefly indicate your reasons for not completing a statutory certificate",
                        field: "notApprovedReason",
                        afterLabel: "The input in this field can be copied directly into the statement of reasons section of the No Certificate submission when completing it.",
                        rows: "5",
                    },
                ],
            },
        ],
    },
];
const soadReportFormPart1Template = {
    signing: soadSigning,
    formBuilderFields: [
        // AD-1.1
        {
            type: "date",
            label: "Date of initial assessment",
            field: "dateOfAssessment",
            afterLabel: "This is the date of your initial visit or the date you started the assessment remotely",
        },
        {
            type: "branch",
            field: "assessmentConducted",
            branches: [
                // AD-1.2
                {
                    fieldValue: true,
                    label: "Yes",
                    hideLabel: true,
                    fieldsOwned: ["assessmentType", "assessmentAddress"],
                    fields: [
                        {
                            type: "dropdown",
                            valueField: "assessmentType",
                            textField: "assessmentTypeText",
                            label: "How was the assessment of the patient conducted?",
                            options: [
                                { label: "In Person", value: "inPerson" },
                                { label: "Phone Call", value: "phoneCall" },
                                { label: "Video Call", value: "videoCall" },
                            ],
                            fieldsOwned: ["assessmentAddress"],
                        },
                        {
                            type: "branch",
                            field: "assessmentType",
                            branches: [
                                {
                                    // AD-1.2.1
                                    fieldValue: "inPerson",
                                    fields: [
                                        {
                                            type: "name-address",
                                            addressLabel: "Where did the assessment take place?",
                                            disableName: true,
                                            field: "assessmentAddress",
                                        },
                                    ],
                                    fieldsOwned: [],
                                    hideLabel: true,
                                    label: "Other",
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        assessmentsField,
        {
            type: "label",
            label: ["Patient Section"],
            labelType: "subtitle",
        },
        // AD-1.4
        {
            type: "dropdown",
            label: "Section of the Mental Health Act",
            valueField: "mentalHealthActSection",
            textField: "mentalHealthActSectionText",
            options: [
                { label: "Section 2 – Admission for Assessment", value: "s2" },
                { label: "Section 3 – Admission for Treatment", value: "s3" },
                { label: "Section 36 – Remand to Hospital", value: "s36" },
                { label: "Section 37 – Hospital Order", value: "s37" },
                {
                    label: "Sections 37/41 – Hospital Order with Restrictions",
                    value: "s37_41",
                },
                { label: "Section 38 – Interim Hospital Order", value: "s38" },
                {
                    label: "Section 45A – Hospital and Limitation Direction",
                    value: "s45a",
                },
                { label: "Section 47 – Transfer Direction", value: "s47" },
                {
                    label: "Sections 47/49 – Transfer with Restrictions",
                    value: "s47_49",
                },
                {
                    label: "Section 48 – Transfer of Unsentenced Prisoners",
                    value: "s48",
                },
                {
                    label: "Sections 48/49 – Transfer with Restrictions (Unsentenced)",
                    value: "s48_49",
                },
                {
                    label: "Criminal Procedure and Investigations Act 1996 (CPIA)",
                    value: "cpia",
                },
                { label: "Community Treatment Order (CTO)", value: "cto" },
                { label: "Informal/Voluntary Admission", value: "informal" },
            ],
        },
        {
            type: "label",
            label: ["Patient Ethnicity"],
            labelType: "subtitle",
        },
        // AD-1.5
        {
            type: "dropdown",
            label: "What is the patient's ethnic group?",
            valueField: "ethnicity",
            textField: "ethnicityText",
            options: [
                { label: "White - British", value: "A" },
                { label: "White - Irish", value: "B" },
                { label: "White - Any other White background", value: "C" },
                { label: "Mixed - White and Black Caribbean", value: "D" },
                { label: "Mixed - White and Black African", value: "E" },
                { label: "Mixed - White and Asian", value: "F" },
                { label: "Mixed - Any other mixed background", value: "G" },
                { label: "Asian or Asian British - Indian", value: "H" },
                { label: "Asian or Asian British - Pakistani", value: "J" },
                { label: "Asian or Asian British - Bangladeshi", value: "K" },
                {
                    label: "Asian or Asian British - Any other Asian background",
                    value: "L",
                },
                { label: "Black or Black British - Caribbean", value: "M" },
                { label: "Black or Black British - African", value: "N" },
                {
                    label: "Black or Black British - Any other Black background",
                    value: "P",
                },
                { label: "Other Ethnic Groups - Chinese", value: "R" },
                { label: "Other Ethnic Groups - Any other ethnic group", value: "S" },
                { label: "Not stated", value: "Z" },
                { label: "Not known", value: "99" },
            ],
        },
        // AD-1.6
        {
            type: "branch",
            label: "How did you determine the patient's ethnicity?",
            field: "ethnicityDetermined",
            branches: [
                {
                    fieldValue: "observedOrAssumed",
                    label: "Observed/assumed",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: "selfReported",
                    label: "Self reported by patient",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: "familyOrFriend",
                    label: "Provided by family member or friend",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: "healthcareProfessional",
                    label: "Provided by healthcare professional",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: "notKnown",
                    label: "Not known",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: "other",
                    label: "Other (please specify)",
                    fieldsOwned: ["ethnicityDeterminedOther"],
                    fields: [
                        {
                            type: "textbox",
                            label: "Please specify how you determined the patient's ethnicity",
                            field: "ethnicityDeterminedOther",
                            rows: "5",
                        },
                    ],
                },
            ],
        },
        {
            type: "label",
            label: ["Assessment Delays"],
            labelType: "subtitle",
        },
        {
            type: "branch",
            label: "Were you delayed due to issues outside of your control in undertaking this assessment?",
            field: "delayedOutsideSoadControl",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: [...assessmentDelayDetailFields],
                    fields: [
                        {
                            type: "multi-select",
                            label: "What caused the delay? (Select all that apply)",
                            options: assessmentDelayCauseOptions.map((o) => ({
                                field: o.field,
                                label: o.label,
                            })),
                        },
                        {
                            type: "textbox",
                            label: "Please provide any further details, especially if 'Other' was selected.",
                            field: "delayDetails",
                            rows: "5",
                        },
                        {
                            type: "textbox",
                            label: "Approximately how many days was the assessment delayed?",
                            field: "delayDays",
                            numeric: true,
                            min: minAssessmentDelayDays,
                            suffix: "days",
                            narrow: true,
                        },
                    ],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [...assessmentDelayDetailFields],
                    fields: [],
                },
            ],
        },
    ],
    dataSchema: soadReportFormPart1DataSchema,
};
const soadReportFormPart2Template = {
    signing: soadSigning,
    formBuilderFields: [
        {
            // DH-1.1
            type: "textbox",
            label: "Please provide details of the patient's history and diagnosis",
            field: "diagnosis",
            afterLabel: "Please include relevant clinical history and diagnosis from documentation provided and verbal information provided by AC/RC/Consultees.",
            rows: "5",
        },
        {
            // DH-1.1a
            type: "textbox",
            label: "What is your assessment of the diagnosis?",
            field: "assessment",
            afterLabel: "The input in this field can be copied directly into the statement of reasons section of the Statutory Certificate when completing it.",
            rows: "5",
        },
    ],
    dataSchema: soadReportFormPart2DataSchema,
};
const soadReportFormPart3Template = {
    signing: soadSigning,
    formBuilderFields: [
        // SA-1.1
        {
            type: "label",
            label: ["Current Treatment at Time of Visit"],
            labelType: "subtitle",
        },
        {
            type: "dropdown",
            label: "What did the provider request a second opinion for?",
            valueField: "secondOpinionRequested",
            textField: "secondOpinionRequestedText",
            options: [
                { label: "Medication", value: "medication" },
                { label: "Electroconvulsive Therapy (ECT)", value: "ect" },
                { label: "Both", value: "both" },
            ],
        },
        // CP-1.1
        {
            type: "textbox",
            label: "Describe the current treatment at the time of your visit, including any medication by drug name, route, dosage and frequency and/or ECT being given to the patient.",
            field: "currentTreatment",
            rows: "5",
        },
        {
            type: "label",
            label: ["AC’s Proposed Treatment Plan:"],
            labelType: "subtitle",
        },
        // CP-1.3
        {
            type: "branch",
            label: "Is the AC's proposed treatment plan different from the current treatment?",
            field: "proposedTreatmentDifference",
            branches: [
                // CP-1.3.1
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: ["describeProposedTreatmentPlan"],
                    fields: [
                        {
                            type: "textbox",
                            label: "Describe the AC's proposed treatment, including any medication by drug name, route, dosage and frequency and/or ECT being given to the patient",
                            field: "describeProposedTreatmentPlan",
                            rows: "5",
                        },
                    ],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
    ],
    dataSchema: soadReportFormPart3DataSchema,
};
const soadReportFormPart4Template = {
    signing: soadSigning,
    formBuilderFields: [
        {
            type: "label",
            label: ["AC and RC Details"],
            labelType: "subtitle",
        },
        // CD-1.1
        {
            type: "textbox",
            label: "Name of Responsible Clinician",
            field: "responsibleClinicianName",
        },
        // CD-1.2
        {
            type: "branch",
            label: "Is the Responsible Clinician the Approved Clinician in charge of treatment?",
            field: "isResponsibleClinicianAC",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: [],
                    fields: [],
                },
                // CD-1.3
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: ["acName"],
                    fields: [
                        {
                            type: "textbox",
                            label: "Name of Approved Clinician",
                            field: "acName",
                        },
                    ],
                },
            ],
        },
        // CD-1.4
        {
            type: "branch",
            label: "Did you consult with the Approved Clinician?",
            field: "consultedWithAC",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: [],
                    fields: [],
                },
                // CD-1.4.1
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: ["reasonForNotConsultingAC"],
                    fields: [
                        {
                            type: "textbox",
                            label: "Please explain why you did not consult with the Approved Clinician",
                            field: "reasonForNotConsultingAC",
                            rows: "5",
                        },
                    ],
                },
            ],
        },
        {
            type: "label",
            label: ["First Consultee Details"],
            labelType: "subtitle",
        },
        // CD-1.5
        {
            type: "textbox",
            label: "Name of First Consultee",
            field: "firstConsulteeName",
            afterLabel: "This must be a person who has been professionally concerned with the patient's medical treatment.",
        },
        // CD-1.6
        {
            type: "textbox",
            label: "Profession of First Consultee",
            field: "firstConsulteeProfession",
            afterLabel: "For T3 and T6 forms, this consultee must be a nurse (who is not the Responsible Clinician or Approved Clinician in charge of the treatment), in line with s58(4) and s58A(6) respectively.",
        },
        {
            type: "label",
            label: ["Second Consultee Details"],
            labelType: "subtitle",
        },
        // CD-1.7
        {
            type: "textbox",
            label: "Name of Second Consultee",
            field: "secondConsulteeName",
            afterLabel: "This must be a person who has been professionally concerned with the patient's medical treatment.",
        },
        // CD-1.8
        {
            type: "textbox",
            label: "Profession of Second Consultee",
            field: "secondConsulteeProfession",
            afterLabel: "For T3 and T6 forms, this consultee must not be a nurse, registered medical practitioner, Responsible Clinician, or Approved Clinician in charge of the treatment, in line with s58(4) and s58A(6) respectively.",
        },
        { type: "label", label: ["Additional Person"], labelType: "subtitle" },
        additionalPersonField,
    ],
    dataSchema: soadReportFormPart4DataSchema,
};
const soadReportFormPart5Template = {
    signing: soadSigning,
    formBuilderFields: [
        {
            type: "label",
            label: ["Recap of the Treatment Plans"],
            labelType: "subtitle",
        },
        {
            type: "readonly",
            textFn: (_data, context) => (() => {
                const currentTreatment = context.previousPartsFormData.find((s) => s.section === "currentAndProposedTreatment")?.data?.currentTreatment;
                return currentTreatment && currentTreatment.trim()
                    ? currentTreatment.split("\n")
                    : [
                        "Please complete the Current and Proposed Treatment section to view current treatment here.",
                    ];
            })(),
            label: "Current treatment at time of visit",
            afterLabel: "Taken from the ‘Current & Proposed Treatment’ section which you completed earlier.",
        },
        // SA-1.2
        {
            type: "readonly",
            textFn: (_data, context) => (() => {
                const isProposedTreatmentPlanDifferent = context.previousPartsFormData.find((s) => s.section === "currentAndProposedTreatment")?.data?.proposedTreatmentDifference;
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
                if (isProposedTreatmentPlanDifferent === false) {
                    return [
                        "You have indicated that the current treatment is the same as the AC’s proposed treatment plan",
                    ];
                }
                const describeProposedTreatmentPlan = context.previousPartsFormData.find((s) => s.section === "currentAndProposedTreatment")?.data?.describeProposedTreatmentPlan;
                return describeProposedTreatmentPlan &&
                    describeProposedTreatmentPlan.trim()
                    ? describeProposedTreatmentPlan.split("\n")
                    : [
                        "Please complete the Current and Proposed Treatment section to view the AC’s proposed treatment here.",
                    ];
            })(),
            label: "The Approved Clinician’s description of their proposed treatment plan",
            afterLabel: "Taken from the ‘Current & Proposed Treatment’ section which you completed earlier.",
        },
        {
            type: "label",
            label: ["The plan you are approving"],
            labelType: "subtitle",
        },
        // SA-1.3
        {
            type: "branch",
            label: "Confirm the type of second opinion carried out",
            field: "secondOpinionCarriedOut",
            branches: [
                {
                    fieldValue: "medication",
                    label: "Medication",
                    fieldsOwned: [
                        "planAuthorisedMedication",
                        "bnfLimits",
                        "bnfLimitsFor",
                        "offLabelMedication",
                    ],
                    fields: [
                        ...approvedPlan(authorisingMedicationFields, [
                            "planAuthorisedMedication",
                            "bnfLimits",
                            "bnfLimitsFor",
                            "offLabelMedication",
                        ]),
                        ...patientConsentStatusFields({ possibleConflict: false }),
                    ],
                },
                {
                    fieldValue: "ect",
                    label: "Electroconvulsive Therapy (ECT)",
                    fieldsOwned: ["planAuthorisedEct", "moreThan12Applications"],
                    fields: [
                        ...approvedPlan(authorisingEctFields, [
                            "planAuthorisedEct",
                            "moreThan12Applications",
                        ]),
                        ...patientConsentStatusFields({ possibleConflict: true }),
                    ],
                },
                {
                    fieldValue: "both",
                    label: "Both",
                    fieldsOwned: [
                        "planAuthorisedMedication",
                        "bnfLimits",
                        "bnfLimitsFor",
                        "offLabelMedication",
                        "planAuthorisedEct",
                        "moreThan12Applications",
                    ],
                    fields: [
                        ...approvedPlan([...authorisingMedicationFields, ...authorisingEctFields], [
                            "planAuthorisedMedication",
                            "bnfLimits",
                            "bnfLimitsFor",
                            "offLabelMedication",
                            "planAuthorisedEct",
                            "moreThan12Applications",
                        ]),
                        ...patientConsentStatusFields({ possibleConflict: true }),
                    ],
                },
            ],
        },
        {
            type: "label",
            label: ["Additional second opinion details"],
            labelType: "subtitle",
        },
        {
            type: "branch",
            label: "Do you wish to time-limit the certificate?",
            field: "timeLimitCertificate",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: [
                        "timeLimitCertificateDuration",
                        "timeLimitCertificateReason",
                    ],
                    fields: [
                        {
                            type: "textbox",
                            field: "timeLimitCertificateDuration",
                            label: "Please outline the time-limit",
                            rows: "5",
                            afterLabel: "The input in this field can be copied directly into the description of treatment section of the Statutory Certificate when completing it.",
                        },
                        {
                            type: "textbox",
                            field: "timeLimitCertificateReason",
                            label: "Please outline the reason for the time-limit",
                            rows: "5",
                            afterLabel: "The input in this field can be copied directly into the statement of reasons section of the Statutory Certificate when completing it.",
                        },
                    ],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
        // SA-1.12
        {
            type: "branch",
            label: "Do you wish the AC to submit an interim s61 report to the CQC?",
            field: "interimS61Required",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: ["interimS61DueDate", "interimS61Reasons"],
                    fields: [
                        // SA-1.12.1
                        {
                            type: "date",
                            label: "By what date?",
                            field: "interimS61DueDate",
                        },
                        // SA-1.12.2
                        {
                            type: "textbox",
                            label: "Please summarise your reasons for this",
                            afterLabel: "The input in this field can be copied directly into the description of treatment section of the Statutory Certificate when completing it. If this is your intention you may want to start your reasoning with 'I want the AC to submit a s61 by X date, for the following reasons...'",
                            field: "interimS61Reasons",
                            rows: "5",
                        },
                    ],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
        // SA-1.13
        {
            type: "textbox",
            label: "Note any difficulties/concerns or good practice in completing the second opinion which the Provider & CQC may wish to be aware of",
            field: "notesForCqc",
            afterLabel: "If there is a safeguarding concern, this must be reported directly to the hospital managers & to CQC.",
            rows: "5",
        },
    ],
    dataSchema: soadReportFormPart5DataSchema,
};
const soadReportFormPart6Template = {
    signing: soadSigning,
    formBuilderFields: [
        {
            type: "textbox",
            field: "notes",
            rows: "5",
        },
    ],
    dataSchema: soadReportFormPart6DataSchema,
};
export const soadReportFormV1Base = {
    id: "soad-report-form",
    category: "soad",
    version: "1.0",
    status: "active",
    formName: "Soad Report Form",
    section: "",
    parts: [
        soadReportFormPart1Template,
        soadReportFormPart2Template,
        soadReportFormPart3Template,
        soadReportFormPart4Template,
        soadReportFormPart5Template,
        soadReportFormPart6Template,
    ],
    description: "",
    extractGenericData: () => {
        return {
            standalone: {},
        };
    },
};
//# sourceMappingURL=srf.js.map